<template>
  <v-app>
    <v-app-bar
      app
      color="secondary"
      dark
      v-if="isAuthenticated"
      fixed
      clipped-left
      dense
    >
      <v-app-bar-nav-icon @click="showDrawer = !showDrawer"></v-app-bar-nav-icon>
      <div class="d-flex align-center">
        <v-img
          :alt="$properties.appTitle"
          class="shrink mr-2"
          contain
          :src="require('./assets/stayin-office-logo-2021-130px.png')"
          width="130"
        />
      </div>

      <v-spacer></v-spacer>

      <notifications-menu
        v-if="hasAnyPermission('NOTIFICATION_VIEW')"
        class="mr-2"
      />

      <header-user-menu></header-user-menu>
      <v-btn
        v-if="isDevelopment"
        text
        small
        @click="showTranslationModal = true"
      >
        <v-icon small left>mdi-message-draw</v-icon>Translation Helper
      </v-btn>

    </v-app-bar>

    <navigation-drawer v-if="isAuthenticated" :show-drawer.sync="showDrawer" ref="navigationDrawer"/>

    <v-overlay :value="isGlobalLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-main class="pb-16 grey lighten-5">
      <keep-alive :include="componentsKeptAlive" max="1">
        <router-view class="mt-2" :key="$route.fullPath"></router-view>
      </keep-alive>
    </v-main>

    <error-dialog ref="errorDialog"/>

    <translation-helper v-if="isDevelopment" :enabled.sync="showTranslationModal"></translation-helper>
  </v-app>
</template>

<script>
import {RouterUtils} from './utils/router-utils';
import { mapGetters } from 'vuex';
import HeaderUserMenu from './HeaderUserMenu';
import TranslationHelper from './components/TranslationHelper';
import {DevUtils} from './utils/dev-utils';
import ErrorDialog from './components/ErrorDialog';
import {MainUtils} from '@/utils/main-utils';
import NavigationDrawer from '@/NavigationDrawer';
import routes from '@/router/routes';
import NotificationsMenu from '@/NotificationsMenu';

export default {
  name: 'App',
  mixins: [
    RouterUtils,
    DevUtils,
    MainUtils
  ],
  components: {
    NotificationsMenu,
    NavigationDrawer,
    ErrorDialog,
    TranslationHelper,
    HeaderUserMenu,
  },

  data: () => ({
    showTranslationModal: false,
    refererRoute: null,
    showDrawer: true,
    componentsKeptAlive: []
  }),

  computed: {
    ...mapGetters({
      auth: 'auth',
      isAuthenticated: 'isAuthenticated',
      isGlobalLoading: 'isGlobalLoading'
    }),
  },

  mounted () {
    this.$nextTick(() => {
      this.$root.$errorDialog = this.$refs.errorDialog.open;
    })

    fetch("/", {cache: "reload"});

    this.setComponentsKeptAlive();
  },

  methods: {
    setRefererRoute () {
      if (!(this.isCurrentRoute('Login') || this.isCurrentRoute('NotFound'))) {
        this.refererRoute = this.getRouter().currentRoute;
      }
    },
    /*
     * Set vue components to be kept-alive (state stored) based on menu items
     */
    setComponentsKeptAlive () {
      let componentsKeptAlive = [];
      this.$nextTick(() => {
        if(this.$refs.navigationDrawer) {
          this.$refs.navigationDrawer.allMenuItems.forEach(menuItem => {
            routes.forEach(route => {
              if (menuItem.route === route.name) {
                let componentName = route.component.name;
                if (!componentsKeptAlive.includes(componentName)) {
                  componentsKeptAlive.push(componentName);
                }
              }
            })
          });
        }

        // Manual additions
        componentsKeptAlive.push(...[
          'SettlementDetailsPage'
        ]);

        this.componentsKeptAlive = componentsKeptAlive;
      });
    }
  },

  watch: {
    isAuthenticated () {
      if (!this.isAuthenticated) {
        this.setRefererRoute();
        this.replaceToRoute('Login')
      } else {
        this.setComponentsKeptAlive();
        if (this.auth.user && this.auth.user.role === 'ADMIN') {
          this.replaceToRoute('Users');
        } else {
          if (this.refererRoute) {
            this.replaceToRoute(this.refererRoute.name, this.refererRoute.params, this.refererRoute.query);
          } else {
            this.gotoLandingPage();
          }
        }
      }
    },
    $route: {
      immediate: true,
      handler () {
        if (!this.isAuthenticated && !this.isCurrentRoute('Login')) {
          this.setRefererRoute();
          this.replaceToRoute('Login')
        }

        // Reset window title. This may be overwritten in a page's component
        document.title = this.$properties.appTitle;
      }
    }
  }
};
</script>

<style>
.unwrappable { white-space: nowrap; }

.v-application a { text-decoration: none; }

.v-card > .v-card__text .form-input-input,
.v-card .v-card__subtitle .form-input-input {
  color: initial;
}

.v-card__subtitle .form-input-input,
.v-card__text .form-input-input {
  font-size: initial;
}

.tabs-transparent.v-tabs > .v-tabs-bar {
  background-color: transparent;
}
</style>
